/**
 * App — router config. Single console, no legacy routes.
 *
 * Sprint 23.7 — nav rework: `/welcome` onboarding flow + `/library` index added;
 * `/wrapped-apis` renamed to `/connections`; `/agents` (dashboard), `/prompts`,
 * and `/members` deprecated and redirected (the `/agents/:nodeId` cockpit stays).
 */

import { createElement, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router";
import { AuthCallback } from "./lib/components/AuthCallback";
import { ApprovalCallback } from "./lib/components/ApprovalCallback";
import { LoginPage } from "./lib/components/LoginPage";
import { V3Layout } from "./v3/routes/V3Layout";
import { WorkflowTemplatesPage } from "./v3/routes/WorkflowTemplatesPage";
import { WorkflowTemplateDetailPage } from "./v3/routes/WorkflowTemplateDetailPage";
import { V3IndexRedirect } from "./v3/routes/V3IndexRedirect";
import { CockpitPage } from "./v3/routes/CockpitPage";
import { LibraryMemoryPage } from "./v3/routes/LibraryMemoryPage";
import { LibraryPage } from "./v3/routes/LibraryPage";
import { ScopePage } from "./v3/routes/ScopePage";
import { ActivityPage } from "./v3/routes/ActivityPage";
import { NoAgentsPage } from "./v3/routes/NoAgentsPage";
import { TablePage } from "./v3/routes/TablePage";
import { MembersPage } from "./v3/routes/MembersPage";
import { MyTasksPage } from "./v3/routes/MyTasksPage";
import { WorkforcePage } from "./v3/routes/WorkforcePage";
import { WorkspaceHomePage } from "./v3/routes/WorkspaceHomePage";
import { HireWizardPage } from "./v3/routes/HireWizardPage";
import { TablesDashboardPage } from "./v3/routes/TablesDashboardPage";
import { RowDetailFullPage } from "./v3/routes/RowDetailFullPage";
import { TaskDetailPage } from "./v3/routes/TaskDetailPage";
import { RoutineView } from "./v3/routes/RoutineView";
import { WorkspaceSettingsPage } from "./v3/routes/WorkspaceSettingsPage";
import { EndpointsSettingsPage } from "./v3/routes/EndpointsSettingsPage";
import { ConnectionsDashboardPage } from "./v3/routes/ConnectionsDashboardPage";
import { SettingsPage } from "./v3/routes/app/SettingsPage";
import { WrappedApiDetailPage } from "./v3/routes/WrappedApiDetailPage";
import { McpConnectionDetailPage } from "./v3/routes/McpConnectionDetailPage";
import { CacheHealthPage } from "./v3/routes/CacheHealthPage";
// №16 D-WF13 — legacy workflow routes retired.
import { useIsOwner } from "./v3/hooks/use-is-owner";
import { WelcomeShell } from "./v3/components/welcome/WelcomeShell";
import { WelcomeIntentStep } from "./v3/components/welcome/WelcomeIntentStep";
import { WelcomeThemeStep } from "./v3/components/welcome/WelcomeThemeStep";
import { WelcomeHireStep } from "./v3/components/welcome/WelcomeHireStep";
// Spec 10.2.1 — the App Shell (product surface), a sibling of the console.
import { AppShellRoot } from "./v3/routes/app/AppShellRoot";
import { NeedsYouPage } from "./v3/routes/app/NeedsYouPage";
import { WorkforceBoardPage } from "./v3/routes/app/WorkforceBoardPage";
import { HandledPage } from "./v3/routes/app/HandledPage";
// Spec №11 (D-M4) — ledger workbenches now come from the workbench registry;
// their routes are generated below instead of hand-listed.
import { WORKBENCH_REGISTRY } from "./v3/routes/app/workbench-registry";
// Spec №11 (Stage 3) — generic declared-view Place list + four-plane record page.
import { PlaceListPage } from "./v3/routes/app/PlaceListPage";
import { PlaceRecordRoute } from "./v3/routes/app/PlaceRecordPage";
import { WorkflowFullView } from "./v3/routes/app/WorkflowFullView";
import { ProgramView } from "./v3/routes/app/ProgramView";
import { StaffDeskPage } from "./v3/routes/app/StaffDeskPage";
import { TaskThreadPage } from "./v3/routes/app/TaskThreadPage";
import { ChatSubjectPage } from "./v3/routes/app/ChatSubjectPage";
import { IntakeSubjectPage } from "./v3/routes/app/IntakeSubjectPage";
import { AuthGuard } from "./lib/components/AuthGuard";

/**
 * Sprint 24 — carry a wrapped-API `:slug` to its canonical `/connections/w/:slug`
 * detail path. Covers both the legacy `/wrapped-apis/:slug` and the pre-Sprint-24
 * `/connections/:slug` bookmarks (Q11 — 302 from the old wrapped-only path).
 */
function WrappedApiSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/connections/w/${slug ?? ""}`} replace />;
}

/**
 * Sprint 24.13 — owner-only route guard for /admin/cache-health. Members get
 * redirected home (the backing get_cache_health tool is also owner-gated by ACL,
 * so this is the UX layer of a defence-in-depth pair).
 */
function RequireOwner({ children }: { children: ReactNode }) {
  const isOwner = useIsOwner();
  if (!isOwner) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes — outside AuthGuard */}
        <Route path="auth/callback" element={<AuthCallback />} />
        {/* Spec 10.2.1.2 — approval magic-link landing. */}
        <Route path="auth/approve" element={<ApprovalCallback />} />
        <Route path="auth/login" element={<LoginPage />} />

        {/* Spec 10.2.1 — App Shell (product surface). Own chrome (two-axis nav,
            Needs-You home); reuses AuthGuard, gates on ledger_enabled. */}
        <Route path="app" element={<AppShellRoot />}>
          <Route index element={<NeedsYouPage />} />
          {/* №53 (53.2) — Workforce as a primary /app Place. The maker surface's
              own `/workforce` stays where it is: that one configures hires, this
              one is where a principal reads them. */}
          <Route path="workforce" element={<WorkforceBoardPage />} />
          {/* №53 (53.4) — Handled: the day's receipt. Direction B, as ruled (E-31). */}
          <Route path="handled" element={<HandledPage />} />
          {/* Spec №11 (D-M4) — ledger workbenches generated from the registry
              (paths unchanged; reachable exactly as today). */}
          {WORKBENCH_REGISTRY.filter((w) => w.chrome === "shell").map((w) => (
            <Route key={w.name} path={w.path} element={createElement(w.component)} />
          ))}
          {/* Spec №11 (Stage 3) — generic declared Place list + four-plane record.
              Any installed module's `view` sitemap target opens these. */}
          <Route path="place/:entity" element={<PlaceListPage />} />
          {/* №29 (S2) — ONE record route. The three type-baked routes it replaces
              (`place/matter/:id`, `place/invoice/:id`, `place/fee-arrangement/:id`)
              are retired; the entity type now picks the renderer from a registry
              instead of from the path. See record-renderers.ts. */}
          <Route path="place/:entity/:id" element={<PlaceRecordRoute />} />
          {/* №29 (S4, §3 ④) — the record's OWN views, as routes rather than in-page tabs.
              Same element as the record itself: which component draws a record is a
              registry read, and which SECTION it opens on is a route read. The patterns
              are generic; `record-subviews.ts` declares which entity types offer which,
              and `PlaceRecordRoute` redirects a URL naming a sub-view this type does not
              have. Static segments, so each is a registered route the coverage gate can
              see — and each outranks nothing (there is no `:view` catch-all to tie with,
              which is the ranking trap S2 removed). */}
          <Route path="place/:entity/:id/parties" element={<PlaceRecordRoute />} />
          <Route path="place/:entity/:id/billing" element={<PlaceRecordRoute />} />
          <Route path="place/:entity/:id/trust" element={<PlaceRecordRoute />} />
          <Route path="place/:entity/:id/register" element={<PlaceRecordRoute />} />
          <Route path="place/:entity/:id/conflicts" element={<PlaceRecordRoute />} />
          <Route path="place/:entity/:id/files" element={<PlaceRecordRoute />} />
          {/* №53.3.1 S0 (E-33, D-CR26) — Details: the record's declared sections
              (Key dates, Team, Effective rates — `RecordView`), evicted from the
              landing view into their own last tab. Same registry-read element as
              every other sub-view. */}
          <Route path="place/:entity/:id/details" element={<PlaceRecordRoute />} />
          {/* №16 S4 (D-WF15 full density) — the deep spine view, read/execute only. */}
          <Route path="place/:entity/:id/process" element={<WorkflowFullView />} />
          {/* №16 S5 (D-WF6) — the Program (journey) view across chained instances. */}
          <Route path="place/:entity/:id/program" element={<ProgramView />} />
          {/* Spec №15.3 — the Staff axis desk (the agent's four-plane record). */}
          <Route path="staff/:agentId" element={<StaffDeskPage />} />
          {/* Spec №22 (D-CL1) — the OPERATIONAL execution thread. Reached from the desk
              Work plane, Needs-You, and the matter Process surface. Operational UX
              lives in /app, never the console. */}
          <Route path="task/:taskId" element={<TaskThreadPage />} />
          {/* №29 (S1) — the CHAT subject (mockup A): the stage IS the conversation.
              One page, four routes, because the conversation belongs to the surface
              it is a conversation ABOUT — `…/chat` under it. Each has a matrix row
              that delegates its anchor/scope/context to the row it was promoted
              from, so moving the conversation onto the stage changes the geometry
              and nothing else. Reached from the rail's Chat tab, not from a nav row. */}
          {/* №29 (S2) — the INTAKE subject (mockup C): two zones, the conversation
              on the stage, nothing running beside it. This is what makes the probe's
              `New…` nav row a live destination. */}
          <Route path="new" element={<IntakeSubjectPage />} />
          <Route path="chat" element={<ChatSubjectPage />} />
          <Route path="place/:entity/chat" element={<ChatSubjectPage />} />
          <Route path="place/:entity/:id/chat" element={<ChatSubjectPage />} />
          <Route path="staff/:agentId/chat" element={<ChatSubjectPage />} />

          {/* №53 (53.0, D-CR14 / D-CR21) — SETTINGS, and the console absorbed into it.
              №35's payload: David, 2026-08-09 *"make the console a settings page inside
              the main app"*, and 2026-08-19 *"the '.app' route will survive, and the
              '/console' will be completely removed."*

              The seven components below are the SAME ones the maker surface mounts;
              they are RE-PARENTED here, not rebuilt, so the user administers the firm
              without leaving `/app` — which is what makes deleting the rail's `Console`
              link a completion rather than a removal of function.

              Re-skinning what renders inside each is explicitly later work: they still
              carry their maker-era chrome. What lands now is the page, the menu entry
              and the re-parenting. Their `/` routes stay registered until №35 removes
              the maker surface outright, so nothing that links to them breaks in between. */}
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/workspace" element={<WorkspaceSettingsPage />} />
          <Route path="settings/members" element={<MembersPage />} />
          <Route path="settings/endpoints" element={<EndpointsSettingsPage />} />
          <Route path="settings/connections" element={<ConnectionsDashboardPage />} />
          <Route path="settings/tables" element={<TablesDashboardPage />} />
          <Route path="settings/workflows" element={<WorkflowTemplatesPage />} />
          <Route path="settings/library" element={<LibraryPage />} />
        </Route>

        {/* Spec 10.3.1 §1 — statutory report print preview. OUTSIDE the App Shell
            layout so no shell chrome leaks into the print output; own AuthGuard.
            Generated from the registry (chrome:"bare"). */}
        {WORKBENCH_REGISTRY.filter((w) => w.chrome === "bare").map((w) => (
          <Route key={w.name} path={w.path} element={<AuthGuard>{createElement(w.component)}</AuthGuard>} />
        ))}

        {/* Console */}
        <Route path="/" element={<V3Layout />}>
          <Route index element={<V3IndexRedirect />} />

          {/* Sprint 23.7 — onboarding flow (new workspaces only; the shell guards). */}
          <Route path="welcome" element={<WelcomeShell />}>
            <Route index element={<Navigate to="/welcome/intent" replace />} />
            <Route path="intent" element={<WelcomeIntentStep />} />
            <Route path="theme" element={<WelcomeThemeStep />} />
            <Route path="hire" element={<WelcomeHireStep />} />
          </Route>

          <Route path="agents/:nodeId" element={<CockpitPage />} />
          {/* Sprint 25.6 — collection sub-nav tabs (Pages/Memories/Files/Activity/
              Settings). Deep-linkable; browser-back works. Overview is the bare
              `agents/:nodeId` above. Distinct from the 4-segment tables route. */}
          <Route path="agents/:nodeId/:tab" element={<CockpitPage />} />
          <Route path="library/:id" element={<LibraryMemoryPage />} />
          {/* Sprint 23.7 — workspace-wide Library index (pages + memories + files + prompts). */}
          <Route path="library" element={<LibraryPage />} />
          <Route path="scope" element={<ScopePage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="my-tasks" element={<MyTasksPage />} />
          {/* Sprint 23.8 — workspace home: the supervisor's daily briefing. */}
          <Route path="home" element={<WorkspaceHomePage />} />
          {/* Sprint 23 — Workforce route: every agent as a card with live status. */}
          <Route path="workforce" element={<WorkforcePage />} />
          {/* Sprint 23.5 — Hire wizard: conversational composition surface. */}
          <Route path="hire" element={<HireWizardPage />} />
          {/* Sprint 9 — workspace-wide tables dashboard. */}
          <Route path="tables" element={<TablesDashboardPage />} />
          {/* №16.1 — the workflow authoring surface (maker canvas, D-WF14 console). */}
          <Route path="workflows" element={<WorkflowTemplatesPage />} />
          <Route path="workflows/:code" element={<WorkflowTemplateDetailPage />} />
          {/* Sprint 16a → 23.7 — wrapped APIs, now "Connections". */}
          <Route path="connections" element={<ConnectionsDashboardPage />} />
          <Route path="connections/m/:id" element={<McpConnectionDetailPage />} />
          <Route path="connections/w/:slug" element={<WrappedApiDetailPage />} />
          {/* Sprint 24 — 302 old wrapped-only `/connections/:slug` bookmarks → `/connections/w/:slug`. */}
          <Route path="connections/:slug" element={<WrappedApiSlugRedirect />} />
          {/* Sprint 12 — shareable full-page row detail. */}
          <Route path="entities/:typeSlug/:rowId" element={<RowDetailFullPage />} />
          {/* Sprint 26.0 — full-page task detail (same component as the pane). */}
          <Route path="tasks/:id" element={<TaskDetailPage />} />
          {/* Sprint 14 — single-routine read/edit view. */}
          <Route path="routines/:id" element={<RoutineView />} />
          <Route path="settings/workspace" element={<WorkspaceSettingsPage />} />
          {/* Sprint 17 — MCP endpoint topology: copyable per-route URLs. */}
          <Route path="settings/endpoints" element={<EndpointsSettingsPage />} />
          {/* Sprint 23.7 — members moved under Settings. */}
          <Route path="settings/members" element={<MembersPage />} />
          {/* №16 D-WF13 — legacy workflow catalogue/instance routes retired. */}
          {/* Sprint 24.13 — owner-only prompt-cache health dashboard. */}
          <Route
            path="admin/cache-health"
            element={<RequireOwner><CacheHealthPage /></RequireOwner>}
          />
          <Route path="no-agents" element={<NoAgentsPage />} />
          <Route path="agents/:nodeId/tables/:tableId" element={<TablePage />} />

          {/* Sprint 23.7 — deprecated-route redirects. */}
          <Route path="agents" element={<Navigate to="/workforce" replace />} />
          <Route path="prompts" element={<Navigate to="/library?filter=prompts" replace />} />
          <Route path="members" element={<Navigate to="/settings/members" replace />} />
          <Route path="wrapped-apis" element={<Navigate to="/connections" replace />} />
          <Route path="wrapped-apis/:slug" element={<WrappedApiSlugRedirect />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
